import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { CREDIT_PACKAGE_DEFINITIONS, CREDIT_TEXT_LIMITS } from '@icebreaker/shared';
import {
  Button,
  DateTimePicker,
  Field,
  Input,
  Modal,
  Textarea,
  useFormErrors,
} from '@icebreaker/ui';
import { organizationDetailPath } from '@/app/routes';
import type { LeadReferral } from '@/features/sales/model/referral-status';
import {
  calendarDayInstant,
  numericDateOptions,
  numericDateTimeOptions,
} from '@/features/sales/model/sales-view';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import {
  buildGrantBody,
  giftCodeOf,
  giftOnlyAllowed,
  giftRejectionOf,
  inquiryMismatchOf,
  typedCodeInvalid,
  type GiftRejection,
  type GrantChoice,
  type GrantFormState,
  type GrantGiftSource,
} from '../model/grant-body';
import { creditMutationReset, grantCredits } from '../store/credits.slice';
import { ceilingOf } from './credit-summary-card';
import {
  DialogFooter,
  DialogSub,
  InfoBlock,
  ServerBanner,
  UnknownOutcome,
  failureKind,
  fieldErrorPaths,
  isConflict,
  oneYearBefore,
  pack12BurnInstant,
  useStaffName,
  useTodayInIstanbul,
  useReleaseKeyOnSettle,
  useWriteKey,
} from './dialog-parts';
import { GiftBlock, GrantSuccessLines, LeadGiftContent } from './grant-gift-block';
import styles from './organizations.module.css';

const G = 'admin.credit.grantDialog' as const;
const CHOICES: readonly GrantChoice[] = ['single', 'pack_12', 'gift_only'];

/**
 * KREDİ YÜKLE (P6 · 1, referans kodu revizyonu P6r). Tekli, 12'li paket ya da
 * "Yalnız hediye"; tutar ve ödeme alanı YOK.
 *
 * HEDİYE KODU (Story 4): pencere satış adayından açıldıysa (`lead`) adayın
 * kodu ve durumu gelir, geçerli kodda "Hediyeyi ekle" işaretli doğar; kurum
 * detayından açıldıysa kod elle yazılır ve gönderimde doğrulanır. Gövde
 * kuralı `model/grant-body.ts`te: aday kimliği YALNIZ kodla ve YALNIZ adaydan
 * açılışta gider. Yükleme anında kod reddedilirse HİÇBİR kredi yazılmaz,
 * pencere çatışma bandını ve "Hediyesiz devam et"i çizer (gövde değiştiği
 * için yeni yineleme anahtarıyla gider).
 *
 * Sözleşme ya da teklif no İSTEĞE BAĞLI (Story 5). 12'li pakette satın alma
 * günü zorunlu: gelecek gün ve bir yıldan eski gün reddedilir (V7). Gönderim
 * sürerken düğme ve alanlar kapalı; aynı gövde aynı anahtarla gider.
 */
export function GrantCreditsDialog({
  organizationId,
  organizationName,
  lead = null,
  onClose,
}: {
  organizationId: string;
  organizationName: string;
  /** Satış adayından açılışta adayın kod alanları; kurum detayından `null`. */
  lead?: LeadReferral | null;
  onClose: () => void;
}) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const staffName = useStaffName();
  const mutation = useAppSelector((root: RootState) => root.panelCredits.mutations.grant);
  const result = useAppSelector((root: RootState) => root.panelCredits.grantResult);
  const today = useTodayInIstanbul();
  const [choice, setChoice] = useState<GrantChoice>('single');
  const [purchaseDay, setPurchaseDay] = useState(today);
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [giftOn, setGiftOn] = useState(true);
  const [typedCode, setTypedCode] = useState('');
  const [blocked, setBlocked] = useState<GiftRejection | null>(null);
  const [touched, setTouched] = useState(false);

  const source: GrantGiftSource = lead
    ? {
        from: 'lead',
        salesInquiryId: lead.id,
        code: lead.referralCode,
        status: lead.referralCodeStatus,
      }
    : { from: 'organization' };
  const form: GrantFormState = {
    choice,
    purchaseDay,
    reference,
    reason,
    giftOn,
    typedCode,
    giftBlocked: blocked !== null,
  };
  // Kapsam kurumdur: aynı kuruma aynı gövde, kesin yanıta kadar aynı anahtarla gider.
  const writeKey = useWriteKey(`grant:${organizationId}`);
  const outcome = failureKind(mutation.phase, mutation.error);
  const rejection = giftRejectionOf(mutation.error);
  // Kurum detayında "bulunamadı" alanın altına yazılır, diğer retler banttır.
  const giftConflict =
    rejection !== null && !(source.from === 'organization' && rejection === 'not_found');
  const mismatch = source.from === 'lead' && inquiryMismatchOf(mutation.error);

  useEffect(() => {
    dispatch(creditMutationReset('grant'));
    return () => {
      dispatch(creditMutationReset('grant'));
    };
  }, [dispatch]);

  useEffect(() => {
    if (giftConflict && rejection) setBlocked(rejection);
  }, [giftConflict, rejection]);

  useReleaseKeyOnSettle(mutation.phase, outcome, writeKey.release);

  const busy = mutation.phase === 'loading';
  const serverPaths = fieldErrorPaths(mutation.error);
  // Tasarım cümlesi olmayan sunucu kuralı alanın altında genel cümleyle yazılır.
  const serverForm = useFormErrors(mutation.error, [
    'reference',
    'purchaseDay',
    'reason',
    'referralCode',
    'salesInquiryId',
  ]);
  const minDay = oneYearBefore(today);
  const allowGiftOnly = giftOnlyAllowed(source, form);

  let purchaseError: string | undefined;
  if (choice === 'pack_12' && (touched || serverPaths.has('purchaseDay'))) {
    if (purchaseDay === '') purchaseError = t.t(`${G}.purchaseRequired`);
    else if (purchaseDay > today) purchaseError = t.t(`${G}.purchaseFuture`);
    else if (purchaseDay < minDay) purchaseError = t.t(`${G}.purchaseOld`);
    else if (serverPaths.has('purchaseDay')) purchaseError = t.t(`${G}.purchaseOld`);
  }
  let codeError: string | undefined;
  if (source.from === 'organization') {
    if (touched && typedCodeInvalid(source, form)) codeError = t.t(`${G}.giftCodeFormat`);
    else if (rejection === 'not_found') codeError = t.t(`${G}.giftCodeNotFound`);
    else codeError = serverForm.at('referralCode');
  }

  const giftOnlyConflict = giftConflict && choice === 'gift_only';
  /*
   * YENİDEN GÖNDERİM YOLU KAPALI HÂLLER: sonucu bilinmeyen yazma (P6r "Kapat"
   * + "Kredi defterini aç"), "Yalnız hediye" çatışması ve aday eşleşmemesi.
   * Alt şeritte gönder düğmesi yok, ama form açık kalır ve tek metin alanlı
   * formda Enter tarayıcının örtük gönderimini tetikler. Sonucu bilinmeyende
   * alan değiştirilip Enter'a basılırsa gövde değişir, YENİ anahtarla gider
   * ve ilk yazma işlendiyse ikinci hak yazılır. Kapı bu yüzden düğmede değil
   * gönderimin kendisindedir.
   */
  const resendClosed = outcome === 'unknown' || giftOnlyConflict || mismatch;

  const submit = (overrides: Partial<GrantFormState> = {}) => {
    if (busy || resendClosed) return;
    setTouched(true);
    const next = { ...form, ...overrides };
    const invalidPurchase =
      next.choice === 'pack_12' &&
      (next.purchaseDay === '' || next.purchaseDay > today || next.purchaseDay < minDay);
    if (invalidPurchase || typedCodeInvalid(source, next)) return;
    const body = buildGrantBody(source, next);
    const idempotencyKey = writeKey.keyFor(JSON.stringify(body));
    dispatch(grantCredits.request({ organizationId, body: { ...body, idempotencyKey } }));
  };
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    submit();
  };
  // Kutu kaldırılır (ya da alan boşaltılır) ve AYNI paketle hemen yeniden gönderilir.
  const continueWithoutGift = () => {
    if (source.from === 'lead') setGiftOn(false);
    else setTypedCode('');
    submit(source.from === 'lead' ? { giftOn: false } : { typedCode: '' });
  };
  const goOrganization = () => {
    onClose();
    void navigate(organizationDetailPath(organizationId));
  };

  const pack = CREDIT_PACKAGE_DEFINITIONS.pack_12;
  const packCredits = pack.credits ?? 0;
  const dateOptions = numericDateOptions(t.locale);
  const giftCode = giftCodeOf(source, form);
  const leadGift = source.from === 'lead' ? (lead?.referralCodeGiftCredits ?? null) : null;
  const purchaseOk = purchaseError === undefined && purchaseDay !== '';
  const burn = purchaseOk ? t.formatDate(pack12BurnInstant(purchaseDay), dateOptions) : '';
  const paidConfirm =
    choice === 'single'
      ? t.t(`${G}.confirmSingle`, { org: organizationName })
      : choice === 'pack_12' && purchaseOk
        ? t.t(`${G}.confirmPack`, {
            org: organizationName,
            count: packCredits,
            buy: t.formatDate(calendarDayInstant(purchaseDay), dateOptions),
            burn,
          })
        : null;
  let confirm: string | null = paidConfirm;
  // Sunucu yazılan kodu bulamadıysa kod düzeltilene kadar hediye vaat edilmez.
  const typedCodeNotFound = source.from === 'organization' && rejection === 'not_found';
  if (giftCode && !typedCodeInvalid(source, form) && !typedCodeNotFound) {
    const code = giftCode;
    if (leadGift !== null) {
      if (choice === 'gift_only') {
        confirm = t.t(`${G}.confirmGiftOnly`, { org: organizationName, count: leadGift, code });
      } else if (choice === 'single') {
        confirm = t.t(`${G}.confirmSingleGift`, { org: organizationName, count: leadGift, code });
      } else if (purchaseOk) {
        confirm = t.t(`${G}.confirmPackGift`, {
          org: organizationName,
          credits: packCredits,
          count: leadGift,
          code,
          burn,
        });
      }
    } else if (choice === 'gift_only') {
      confirm = t.t(`${G}.confirmTypedGiftOnly`, { org: organizationName, code });
    } else if (paidConfirm) {
      confirm = `${paidConfirm} ${t.t(`${G}.confirmTypedGift`, { code })}`;
    }
  }

  const succeeded = mutation.phase === 'succeeded' && result !== null;
  const sub = t.t(lead ? `${G}.sourceLead` : `${G}.sourceOrganization`, {
    org: organizationName,
  });

  const footer = succeeded ? (
    <DialogFooter
      audit={t.t(`${G}.successAudit`, {
        name: staffName,
        date: t.formatDate(result.grantedAt, numericDateTimeOptions(t.locale)),
      })}
    >
      {lead ? (
        <>
          <Button variant="ghost" onClick={onClose}>
            {t.t('common.close')}
          </Button>
          <Button variant="primary" onClick={goOrganization}>
            {t.t(`${G}.goOrganization`)}
          </Button>
        </>
      ) : (
        <Button variant="ghost" onClick={onClose}>
          {t.t('common.close')}
        </Button>
      )}
    </DialogFooter>
  ) : (
    <DialogFooter audit={t.t('admin.orgs.auditLine', { name: staffName })}>
      {outcome === 'unknown' ? (
        <>
          <Button variant="ghost" onClick={onClose}>
            {t.t('common.close')}
          </Button>
          <Button variant="primary" onClick={lead ? goOrganization : onClose}>
            {t.t(`${G}.openLedger`)}
          </Button>
        </>
      ) : (
        <>
          <Button variant="ghost" disabled={busy} onClick={onClose}>
            {resendClosed ? t.t('common.close') : t.t('admin.orgs.cancel')}
          </Button>
          {resendClosed ? null : giftConflict ? (
            <Button variant="primary" onClick={continueWithoutGift}>
              {t.t(`${G}.continueWithoutGift`)}
            </Button>
          ) : (
            <Button
              variant="primary"
              type="submit"
              form="grant-credits-form"
              loading={busy}
              loadingLabel={t.t(`${G}.submitting`)}
            >
              {outcome === 'rejected' ? t.t('admin.orgs.retry') : t.t(`${G}.submit`)}
            </Button>
          )}
        </>
      )}
    </DialogFooter>
  );

  return (
    <Modal
      open
      title={t.t(`${G}.title`)}
      closeLabel={t.t('common.close')}
      onClose={onClose}
      closeDisabled={busy}
      footer={footer}
    >
      <DialogSub>{sub}</DialogSub>
      {succeeded ? (
        <GrantSuccessLines result={result} organizationName={organizationName} />
      ) : (
        <>
          {giftConflict && rejection ? (
            <ServerBanner tone="warn" strong>
              {t.t(`${G}.conflict`, { reason: t.t(`${G}.conflictReasons.${rejection}`) })}
            </ServerBanner>
          ) : mismatch ? (
            <ServerBanner strong>{t.t(`${G}.inquiryMismatch`)}</ServerBanner>
          ) : isConflict(mutation.error) ? (
            <ServerBanner strong>{t.t('admin.credit.conflict')}</ServerBanner>
          ) : outcome === 'unknown' ? (
            <UnknownOutcome tone="warn" strong reloaded={t.t(`${G}.reloadedUnknown`)} />
          ) : outcome === 'rejected' ? (
            <ServerBanner strong>{t.t(`${G}.serverError`)}</ServerBanner>
          ) : rejection === null && serverForm.summary.length > 0 ? (
            <ServerBanner strong>{serverForm.summary.join(' ')}</ServerBanner>
          ) : null}
          <form id="grant-credits-form" className={styles.form} onSubmit={onSubmit} noValidate>
            <fieldset className={styles.options} disabled={busy}>
              <legend className={styles.srOnly}>{t.t(`${G}.packLabel`)}</legend>
              {CHOICES.map((value) => {
                const off = value === 'gift_only' && !allowGiftOnly;
                const desc =
                  value === 'single'
                    ? t.t(`${G}.singleDesc`, { ceiling: t.formatNumber(ceilingOf('single') ?? 0) })
                    : value === 'pack_12'
                      ? t.t(`${G}.packDesc`, { credits: t.formatNumber(packCredits) })
                      : t.t(off ? `${G}.giftOnlyOff` : `${G}.giftOnlyDesc`);
                return (
                  <label key={value} className={styles.option}>
                    <span className={styles.optionHead}>
                      <input
                        type="radio"
                        name="grant-kind"
                        className={styles.optionRadio}
                        checked={choice === value}
                        disabled={off}
                        aria-labelledby={`grant-kind-${value}`}
                        aria-describedby={`grant-kind-${value}-desc`}
                        onChange={() => setChoice(value)}
                      />
                      <span id={`grant-kind-${value}`}>
                        {t.t(
                          value === 'single'
                            ? `${G}.single`
                            : value === 'pack_12'
                              ? `${G}.pack`
                              : `${G}.giftOnly`,
                        )}
                      </span>
                    </span>
                    <span id={`grant-kind-${value}-desc`} className={styles.optionDesc}>
                      {desc}
                    </span>
                  </label>
                );
              })}
            </fieldset>
            <GiftBlock>
              {lead ? (
                <LeadGiftContent
                  lead={lead}
                  organizationName={organizationName}
                  giftOn={giftOn || choice === 'gift_only'}
                  disabled={busy}
                  locked={choice === 'gift_only'}
                  blocked={blocked}
                  onToggle={setGiftOn}
                />
              ) : (
                <Field
                  id="grant-gift-code"
                  error={codeError}
                  hint={codeError ? undefined : t.t(`${G}.giftFieldHint`)}
                  label={
                    <>
                      {t.t(`${G}.giftField`)}{' '}
                      <span className={styles.optional}>{t.t('admin.orgs.optional')}</span>
                    </>
                  }
                >
                  <Input
                    id="grant-gift-code"
                    className={styles.mono}
                    value={typedCode}
                    placeholder={t.t(`${G}.giftFieldPlaceholder`)}
                    autoComplete="off"
                    spellCheck={false}
                    disabled={busy}
                    invalid={Boolean(codeError)}
                    onChange={(event) => {
                      const value = event.target.value;
                      setTypedCode(value);
                      setBlocked(null);
                      // "Bulunamadı" yazılan koda aittir: kod değişince cümle kalkar.
                      if (rejection === 'not_found') dispatch(creditMutationReset('grant'));
                      if (
                        choice === 'gift_only' &&
                        !giftOnlyAllowed(source, { ...form, typedCode: value, giftBlocked: false })
                      )
                        setChoice('single');
                    }}
                  />
                </Field>
              )}
            </GiftBlock>
            {choice === 'pack_12' ? (
              <Field
                id="grant-purchase"
                label={t.t(`${G}.purchaseDay`)}
                hint={purchaseError ? undefined : t.t(`${G}.purchaseHint`)}
                error={purchaseError}
              >
                <DateTimePicker
                  id="grant-purchase"
                  mode="date"
                  value={purchaseDay}
                  min={minDay}
                  today={today}
                  invalid={Boolean(purchaseError)}
                  disabled={busy}
                  required
                  onChange={setPurchaseDay}
                />
              </Field>
            ) : null}
            <Field
              id="grant-reference"
              error={serverForm.at('reference')}
              label={
                <>
                  {t.t(`${G}.reference`)}{' '}
                  <span className={styles.optional}>{t.t('admin.orgs.optional')}</span>
                </>
              }
            >
              <Input
                id="grant-reference"
                className={styles.mono}
                value={reference}
                maxLength={CREDIT_TEXT_LIMITS.reference}
                disabled={busy}
                invalid={Boolean(serverForm.at('reference'))}
                onChange={(event) => setReference(event.target.value)}
              />
            </Field>
            <Field
              id="grant-reason"
              error={serverForm.at('reason')}
              label={
                <>
                  {t.t(`${G}.reason`)}{' '}
                  <span className={styles.optional}>{t.t('admin.orgs.optional')}</span>
                </>
              }
            >
              <Textarea
                id="grant-reason"
                rows={2}
                value={reason}
                maxLength={CREDIT_TEXT_LIMITS.reason}
                disabled={busy}
                invalid={Boolean(serverForm.at('reason'))}
                onChange={(event) => setReason(event.target.value)}
              />
            </Field>
            {confirm ? <InfoBlock tone="info">{confirm}</InfoBlock> : null}
          </form>
        </>
      )}
    </Modal>
  );
}
